import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { MjmlButtonNodeView } from '@/modules/base/components/inputs/html-input/extensions/mjml-button/mjml-button-node-view'
import type { MjmlButtonOptions } from '@/modules/base/components/inputs/html-input/extensions/mjml-button/types'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mjmlButton: {
      insertMjmlButton: (options: {
        text: string
        href?: string
        mjClass?: string
        cssClass?: string
      }) => ReturnType
    }
  }
}

export default Node.create<MjmlButtonOptions>({
  name: 'mjmlButton',
  group: 'block',
  atom: true,
  selectable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      buttons: [],
    }
  },

  addAttributes() {
    return {
      text: {
        default: 'Button',
        parseHTML: element => {
          // Get text content from the mj-button element
          return element.textContent?.trim() || 'Button'
        },
        renderHTML: () => {
          // Text content is handled in renderHTML method
          return {}
        },
      },
      href: {
        default: '#',
        parseHTML: element => element.getAttribute('href') || '#',
        renderHTML: attributes => {
          if (attributes.href) {
            return { href: attributes.href }
          }
          return {}
        },
      },
      mjClass: {
        default: '',
        parseHTML: element => element.getAttribute('mj-class') || '',
        renderHTML: attributes => {
          if (attributes.mjClass) {
            return { 'mj-class': attributes.mjClass }
          }
          return {}
        },
      },
      cssClass: {
        default: '',
        parseHTML: element => element.getAttribute('css-class') || '',
        renderHTML: attributes => {
          if (attributes.cssClass) {
            return { 'css-class': attributes.cssClass }
          }
          return {}
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'mj-button',
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false

          const text = element.textContent?.trim() || 'Button'
          const href = element.getAttribute('href') || '#'
          const mjClass = element.getAttribute('mj-class') || ''
          const cssClass = element.getAttribute('css-class') || ''

          return { text, href, mjClass, cssClass }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const { text, href, mjClass, cssClass } = node.attrs

    const attributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
      href: href || '#',
      ...(mjClass && { 'mj-class': mjClass }),
      ...(cssClass && { 'css-class': cssClass }),
    })

    return ['mj-button', attributes, text || 'Button']
  },

  addNodeView() {
    return ReactNodeViewRenderer(MjmlButtonNodeView, {
      // Only stop certain events, allow click events to reach the NodeView
      stopEvent: ({ event }) => {
        // Don't stop click events - let them reach our React component
        if (event.type === 'click' || event.type === 'mousedown') {
          return false
        }
        // Stop other events like keydown, etc.
        return true
      },
    })
  },

  addCommands() {
    return {
      insertMjmlButton:
        (options) =>
          ({ commands }) => {
            return commands.insertContent({
              type: this.name,
              attrs: {
                text: options.text || 'Button',
                href: options.href || '#',
                mjClass: options.mjClass || '',
                cssClass: options.cssClass || '',
              },
            })
          },
    }
  },
})
